'use client'

import { type Options, documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import { useMemo } from 'react'

import { NestedFigsThemeProvider, useLmfAnalyticsOnClick } from '@syconium/little-miss-figgy'
import { ResponsiveImage } from '@syconium/little-miss-figgy/dist/components/Image/ResponsiveImage'
import {
  TypeStyleBodyMicro,
  TypeStyleHeadlineExtraExtraSmall,
  TypeStyleHeadlineSmall,
} from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import {
  GetContentfulPageTileSectionQuery,
  PageTileSectionFragment,
  PageTileSectionTileFragment,
  PageTileSectionTileTextAlignment,
  PageTileSectionTileTextPlacement,
} from '../../../../../__generated__/graphql/catalog/graphql'
import { floatToTwoDigitHex } from '../../../../../lib/utils/colors'
import { useStaticAmplitudeTracking } from '../../../../_hooks/useStaticTracking'
import { TrackingLocationProvider } from '../../../../_providers/TrackingLocationProvider.client'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { NextLink } from '../../../navigation/NextLink'
import {
  LIFTED_BUTTONS_DEFAULT_PIXELS,
  TilesLayout,
  useTilesLayoutContext,
} from '../../../tiles/Tiles.client'
import {
  PageSection,
  PageSectionContent,
  sectionSideGutterFromMd,
  sectionSideGutterFromMdAndExpanded,
  sectionSideGutterUntilMd,
} from '../../PageSection.client'
import {
  createDefaultRichTextRenderMark,
  getContentfulRichTextPlainText,
  getNonEmptySanitizedContentfulDocument,
} from '../components/ContentfulStyledTextRichTextUtils'

import {
  Container,
  PageTileSectionTitle,
  StyledButtonRow,
  StyledContentfulStyledLink,
  StyledTileTextContent,
  Tile,
  TileDescriptionList,
  TileDescriptionText,
  TileOverlay,
  TileOverlayContent,
  TileTextBelow,
} from './ContentfulPageTileSection.styles'

import type { Document } from '@contentful/rich-text-types'

export const pageTileSectionTileQueryFragment = gql(`
  fragment PageTileSectionTile on PageTileSectionTile {
    __typename
    ctaLink {
      ...StyledLinkFragment
    }
    ctaLinkHref
    ctaText
    imageAspectRatio
    textPlacement
    textAlignment
    descriptionRichText
    description
    hoverImage
    hoverVideo
    id
    image
    inlineVideo
    inlineVimeoId
    jumboTitle
    linkHref
    name
    titleImage
    textAlignment
    textPlacement
    overlayOpacity
    overlayColor
  }
`)

gql(`
  fragment PageTileSection on PageTileSection {
    __typename
    id
    name
    numColsDesktop
    numColsMobile
    tileAspectRatio
    textAlign
    textPlacement
    tiles {
      ...PageTileSectionTile
    }
    title
    titleTextSize
    variantDesktop
    variantMobile
    titleAlignmentDesktop
    titleAlignmentMobile
    hasHorizontalMargin
    hasVerticalMargin
    hasTileGaps
  }
`)

export type ContentfulPageTileSectionProps = {
  pageSectionIndex: number
  data: PageTileSectionFragment
  shouldUseCollectionStyling?: boolean
}

// There are no dedicated types for these CMS values, in contentful-gateway PageTileSection layout options are type String (no enum)
export type SectionPlacement = 'center' | 'bottom' | 'below'
export type SectionAlignment = 'left' | 'center' | 'right'

/**
 * Helper function to map CMS tile placement values to component values
 * and determine the resolved placement considering fallbacks
 */
const resolveTilePlacement = (
  tilePlacement: PageTileSectionTileTextPlacement | null,
  sectionPlacement: SectionPlacement
): PageTileSectionTileTextPlacement => {
  if (tilePlacement) return tilePlacement

  if (sectionPlacement === 'center') {
    return 'overlayCenter'
  } else if (sectionPlacement === 'bottom') {
    return 'overlayBottom'
  } else {
    return 'below'
  }
}

/**
 * Helper function to resolve text alignment with fallback chain
 */

const resolveTileAlignment = (
  tileAlignment: PageTileSectionTileTextAlignment | null,
  sectionAlignment: SectionAlignment
): PageTileSectionTileTextAlignment => {
  return tileAlignment ?? sectionAlignment
}

// convert each `\n` into an explicit <br />.
const renderTextWithLineBreaks = (text: string) =>
  text
    .split('\n')
    .flatMap((segment, index) => (index > 0 ? [<br key={index} />, segment] : [segment]))

const TileDescriptionRichText = ({ document }: { document: Document }) => {
  const plainText = useMemo(() => getContentfulRichTextPlainText(document), [document])
  const renderMark = useMemo(() => createDefaultRichTextRenderMark(), [])

  const renderOptions: Options = useMemo(() => {
    const listItemRenderOptions: Options = {
      renderMark,
      renderText: renderTextWithLineBreaks,
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_node, children) => (
          <TypeStyleBodyMicro>{children}</TypeStyleBodyMicro>
        ),
      },
    }

    return {
      renderMark,
      renderText: renderTextWithLineBreaks,
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_node, children) => (
          <TileDescriptionText isUppercase={isUppercase(plainText)}>{children}</TileDescriptionText>
        ),
        [BLOCKS.UL_LIST]: (_node, children) => (
          <TileDescriptionList>{children}</TileDescriptionList>
        ),
        [BLOCKS.LIST_ITEM]: node => (
          <li>
            {documentToReactComponents(
              {
                nodeType: BLOCKS.DOCUMENT,
                data: {},
                content: node.content as Document['content'],
              },
              listItemRenderOptions
            )}
          </li>
        ),
      },
    }
  }, [plainText, renderMark])

  return <>{documentToReactComponents(document, renderOptions)}</>
}

export const ContentfulPageTileSection = ({
  data,
  shouldUseCollectionStyling,
}: ContentfulPageTileSectionProps) => {
  const translations = useTranslation()

  const section = data

  const title = section.title
  const titleTextSize = section.titleTextSize
  const titleAlignmentDesktop = section.titleAlignmentDesktop
  const titleAlignmentMobile = section.titleAlignmentMobile

  const hasHorizontalMargin = section.hasHorizontalMargin

  const layoutVariantDesktop =
    section.variantDesktop === 'carousel' ? 'slider' : section.variantDesktop
  const layoutVariantMobile =
    section.variantMobile === 'carousel' ? 'slider' : section.variantMobile
  const layout = { sm: layoutVariantMobile, md: layoutVariantDesktop }
  const tileGaps = section.hasTileGaps
    ? undefined
    : {
        row: {
          sm: 0,
          md: 0,
        },
        column: {
          sm: 0,
          md: 0,
        },
      }

  const numColsDesktop = section.numColsDesktop
  const numColsMobile = section.numColsMobile

  const tiles = section.tiles

  const textPlacement = section.textPlacement

  return (
    <PageSection
      type={data.__typename}
      id={data.id}
      hasVerticalMargin={!shouldUseCollectionStyling && section.hasVerticalMargin}
    >
      <PageSectionContent
        gutterSize={
          hasHorizontalMargin
            ? layoutVariantDesktop === 'slider'
              ? 'expanded'
              : 'default'
            : 'none'
        }
      >
        {title && (
          <>
            <PageTileSectionTitle
              $titleTextSize={titleTextSize}
              $textAlign={titleAlignmentDesktop ?? undefined}
              $mobileTextAlign={titleAlignmentMobile ?? undefined}
            >
              {title}
            </PageTileSectionTitle>
          </>
        )}
      </PageSectionContent>

      <PageSectionContent gutterSize='none'>
        <TilesLayout
          visibleTiles={{ sm: numColsMobile, md: numColsDesktop }}
          layout={layout}
          scrollBackLabel={translations.magnolia.general.scrollBack}
          scrollForwardLabel={translations.magnolia.general.scrollForward}
          liftButtonsPixels={textPlacement === 'below' ? LIFTED_BUTTONS_DEFAULT_PIXELS : 0}
          gutters={{
            sm: !hasHorizontalMargin ? 0 : sectionSideGutterUntilMd,
            md: !hasHorizontalMargin
              ? 0
              : layoutVariantDesktop === 'slider'
              ? sectionSideGutterFromMdAndExpanded
              : sectionSideGutterFromMd,
          }}
          justifyContent={{
            sm: layoutVariantMobile === 'grid' ? 'center' : 'left',
            md: layoutVariantDesktop === 'grid' ? 'center' : 'left',
          }}
          gaps={tileGaps}
        >
          {tiles.map((tile, index) => {
            return (
              <TrackingLocationProvider key={index} position={index + 1}>
                <Tile>
                  {/* PageTileSectionTileContents references context provided from <Tile>, hence why it is wrapped here instead of one component. */}
                  <PageTileSectionTileContents
                    section={section}
                    tile={tile}
                    tileIndex={index}
                    shouldUseCollectionStyling={shouldUseCollectionStyling}
                  />
                </Tile>
              </TrackingLocationProvider>
            )
          })}
        </TilesLayout>
      </PageSectionContent>
    </PageSection>
  )
}

type ContentfulPageTileSection = Extract<
  NonNullable<GetContentfulPageTileSectionQuery['pageViewSection']>,
  { __typename: 'PageTileSection' }
>

const PageTileSectionTileContents: React.FC<{
  tile: PageTileSectionTileFragment
  section: PageTileSectionFragment
  tileIndex: number
  shouldUseCollectionStyling?: boolean
}> = ({ tile, section, tileIndex, shouldUseCollectionStyling }) => {
  const translations = useTranslation()
  const tilesLayoutContext = useTilesLayoutContext()!
  const hasVerticalMargin = section.hasVerticalMargin
  const sectionTilePlacement = section.textPlacement
  const sectionTileAlignment = section.textAlign
  const { textPlacement, textAlignment } = tile
  const tileAspectRatioOverride =
    section.tileAspectRatio && section.tileAspectRatio > 0 ? section.tileAspectRatio : undefined
  const isFullBleed = !section.hasHorizontalMargin
  const hasContentBelow =
    textPlacement === 'below' || (sectionTilePlacement === 'below' && !textPlacement)

  const isTwoUp = section.tiles.length === 2
  const isTwoUpFullBleed = isTwoUp && isFullBleed
  const hasHover = !!(tile.hoverImage || tile.hoverVideo)

  const aspectRatios = {
    portrait: 0.8,
    twoUp: 1.2166,
    square: 1,
  } as const

  const getAspectRatio = (override?: number) => {
    if (override && override > 0) return override
    if (isTwoUpFullBleed) return aspectRatios.square
    if (isTwoUp) return aspectRatios.twoUp
    return aspectRatios.portrait
  }

  const computedAspectRatios = {
    sm: getAspectRatio(tileAspectRatioOverride),
    md: getAspectRatio(tileAspectRatioOverride),
  }

  const resolvedPlacement = resolveTilePlacement(textPlacement, sectionTilePlacement)
  const resolvedAlignment = resolveTileAlignment(textAlignment, sectionTileAlignment)
  const isTileOverlay = resolvedPlacement !== 'below'
  const isBelowPlacementWithLink = !isTileOverlay && tile.linkHref
  const isOverlayWithLink = isTileOverlay && !tile.ctaLink && tile.linkHref
  const imageContentTheme = tile.overlayColor === 'dark' ? 'dark' : 'light'
  const descriptionRichText = getNonEmptySanitizedContentfulDocument(tile.descriptionRichText)

  const tileTextContent = (
    <StyledTileTextContent $isOverlay={isTileOverlay}>
      {tile.jumboTitle && (
        <TypeStyleHeadlineSmall isUppercase={isUppercase(tile.jumboTitle)}>
          {tile.jumboTitle}
        </TypeStyleHeadlineSmall>
      )}
      {tile.name && (
        <TypeStyleHeadlineExtraExtraSmall isUppercase={isUppercase(tile.name)}>
          {tile.name}
        </TypeStyleHeadlineExtraExtraSmall>
      )}
      {descriptionRichText ? (
        <TileDescriptionRichText document={descriptionRichText} />
      ) : (
        tile.description && (
          <TileDescriptionText isUppercase={isUppercase(tile.description)}>
            {tile.description}
          </TileDescriptionText>
        )
      )}
    </StyledTileTextContent>
  )

  const { trackStaticAmplitudeEvent } = useStaticAmplitudeTracking()
  const onClickTileTracking = useLmfAnalyticsOnClick({
    element: 'tile',
    variations: [`textPlacement=${resolvedPlacement}`, `textAlignment=${resolvedAlignment}`],
    label: tile.jumboTitle ?? tile.name ?? tile.description ?? tile.name ?? tile.id,
    href: tile.linkHref,
    onClick: () =>
      trackStaticAmplitudeEvent({ modifiedElement: 'Module Tile', href: tile.linkHref }),
  })

  return (
    <Container
      as={isBelowPlacementWithLink ? NextLink : 'div'}
      href={isBelowPlacementWithLink ? tile.linkHref : undefined}
      onClick={isBelowPlacementWithLink ? onClickTileTracking : undefined}
      $hasHover={hasHover}
      $textAlign={resolvedAlignment}
      $hasVerticalMargin={hasVerticalMargin}
      $hasOverlay={isTileOverlay}
      $shouldUseCollectionStyling={shouldUseCollectionStyling}
      key={tileIndex}
      $aspectRatios={computedAspectRatios}
      $isTwoUp={isTwoUp}
      $hasContentBelow={hasContentBelow}
    >
      {tile.hoverImage && (
        <ResponsiveImage
          alt=''
          loading='lazy'
          src={tile.hoverImage}
          widths={{
            unit: 'vw',
            sm: 100 / tilesLayoutContext.visibleTiles.sm,
            md: 100 / tilesLayoutContext.visibleTiles.md,
          }}
          aspectRatios={null}
        />
      )}
      {tile.hoverVideo && <video src={tile.hoverVideo} autoPlay muted playsInline loop />}
      {tile.image && (
        <ResponsiveImage
          alt=''
          loading='lazy'
          src={tile.image}
          widths={{
            unit: 'vw',
            sm: 100 / tilesLayoutContext.visibleTiles.sm,
            md: 100 / tilesLayoutContext.visibleTiles.md,
          }}
          aspectRatios={computedAspectRatios}
        />
      )}
      <ClientOnlyResponsiveVideo
        playText={translations.magnolia.general.playVideo}
        pauseText={translations.magnolia.general.playVideo}
        inlineVideoDesktop={tile.inlineVideo}
        inlineVideoMobile={tile.inlineVideo}
        inlineVimeoIdDesktop={tile.inlineVimeoId}
        inlineVimeoIdMobile={tile.inlineVimeoId}
      />

      {/** New overlay absolutely positioned, but rendered outside of ImageWrap */}
      {/** Only overlay content should be rendered here */}
      {isTileOverlay && (
        <NestedFigsThemeProvider themeDesktop={imageContentTheme} themeMobile={imageContentTheme}>
          <TileOverlay
            $placement={resolvedPlacement}
            $textAlignment={resolvedAlignment}
            $hexOverlayOpacity={
              tile.overlayOpacity ? floatToTwoDigitHex(tile.overlayOpacity) : null
            }
            // TODO: OE-2073
            as={isOverlayWithLink ? NextLink : 'div'}
            href={isOverlayWithLink ? tile.linkHref : undefined}
            onClick={isOverlayWithLink ? onClickTileTracking : undefined}
          >
            <TileOverlayContent $alignment={resolvedAlignment}>
              {tile.titleImage && (
                <ResponsiveImage
                  src={tile.titleImage}
                  loading='lazy'
                  alt={''}
                  aspectRatios={{ sm: 1, md: 1 }}
                  widths={{
                    unit: 'vw',
                    sm: 100 / tilesLayoutContext.visibleTiles.sm,
                    md: 100 / tilesLayoutContext.visibleTiles.md,
                  }}
                />
              )}
              {tileTextContent}
              <StyledButtonRow $textAlignment={resolvedAlignment}>
                {tile.ctaLink && <StyledContentfulStyledLink {...tile.ctaLink} />}
              </StyledButtonRow>
            </TileOverlayContent>
          </TileOverlay>
        </NestedFigsThemeProvider>
      )}

      {!isTileOverlay && (
        <TileTextBelow $textAlignment={resolvedAlignment} $isTwoUp={isTwoUp}>
          {tileTextContent}
          <StyledButtonRow $textAlignment={resolvedAlignment}>
            {tile.ctaLink && <StyledContentfulStyledLink {...tile.ctaLink} />}
          </StyledButtonRow>
        </TileTextBelow>
      )}
    </Container>
  )
}
